import * as THREE from 'three';
import { shaderUtils } from './shaderLib.js';

export class BlackHoleShader {
    constructor(blackHole, scene, camera) {
        this.blackHole = blackHole;
        this.scene = scene;
        this.camera = camera;
        
        // Create render target for scene rendering
        this.renderTarget = new THREE.WebGLRenderTarget(1024, 1024);
        this.renderTarget.texture.minFilter = THREE.LinearFilter;
        this.renderTarget.texture.magFilter = THREE.LinearFilter;
        
        // Create fullscreen quad with lensing shader
        this.quad = null;
        this.scene2D = null;
        this.init();
    }

    init() {
        // Create a second scene for post-processing
        this.scene2D = new THREE.Scene();
        
        // Create geometry for fullscreen quad
        const geometry = new THREE.PlaneGeometry(2, 2);
        
        // Create material with gravitational lensing shader
        const material = new THREE.ShaderMaterial({
            uniforms: {
                tScene: { value: this.renderTarget.texture },
                uBlackHolePos: { value: this.blackHole.getPosition() },
                uBlackHoleMass: { value: this.blackHole.getMass() },
                uSchwarzschildRadius: { value: this.blackHole.getSchwarzschildRadius() },
                uResolution: { value: new THREE.Vector2() },
                uCameraPos: { value: this.camera.position },
                uCameraMatrix: { value: this.camera.matrixWorldInverse },
                uProjectionMatrix: { value: this.camera.projectionMatrixInverse },
                uDeflectionScale: { value: 1.0 }
            },
            vertexShader: shaderUtils.vertexShader,
            fragmentShader: this.getFragmentShader()
        });
        
        this.quad = new THREE.Mesh(geometry, material);
        this.quad.frustumCulled = false;
        this.scene2D.add(this.quad);
    }

    getFragmentShader() {
        return `
            uniform sampler2D tScene;
            uniform vec3 uBlackHolePos;
            uniform float uBlackHoleMass;
            uniform float uSchwarzschildRadius;
            uniform vec2 uResolution;
            uniform vec3 uCameraPos;
            uniform mat4 uCameraMatrix;
            uniform mat4 uProjectionMatrix;
            uniform float uDeflectionScale;
            
            varying vec2 vUv;
            
            // Physically reasonable constants
            const float EPSILON = 1e-4;
            const float DEFLECTION_SCALE_BASE = 1.0;  // base scale (4GM/c² ≈ r_s)
            const float MAX_DEFLECTION = 0.5;          // max deflection angle in radians (~29°)
            const float CAPTURE_FACTOR = 1.05;         // rays with b < 1.05 * r_s are captured
            const float PHOTON_SPHERE_FACTOR = 1.5;    // ring at 1.5x Schwarzschild radius
            const float RING_WIDTH = 0.2;              // width of Einstein ring band
            const float RING_BRIGHTNESS = 0.6;         // intensity of ring enhancement
            
            // Convert screen UV to world-space ray direction
            vec3 getWorldRayDirection(vec2 uv) {
                // Convert UV to NDC (Normalized Device Coordinates)
                vec2 ndc = uv * 2.0 - 1.0;
                
                // Use inverse projection to convert NDC to view space
                // For a ray, we use z=1.0 (far plane) to get direction
                vec4 clipPos = vec4(ndc, 1.0, 1.0);
                vec4 viewPos = uProjectionMatrix * clipPos;
                viewPos /= viewPos.w;
                
                // Convert view space direction to world space
                // uCameraMatrix is matrixWorldInverse (world-to-view)
                // For direction vectors, we need view-to-world = transpose of upper 3x3
                // But actually, for direction vectors: worldDir = (viewMatrix^-1)^T * viewDir
                // Since viewMatrix = matrixWorldInverse, we need matrixWorld's upper 3x3
                // However, we only have matrixWorldInverse, so we transpose the upper 3x3
                mat3 viewToWorld = transpose(mat3(
                    uCameraMatrix[0].xyz,
                    uCameraMatrix[1].xyz,
                    uCameraMatrix[2].xyz
                ));
                
                vec3 viewDir = normalize(viewPos.xyz);
                vec3 worldDir = viewToWorld * viewDir;
                
                return normalize(worldDir);
            }
            
            // Rodrigues rotation formula: rotate vector v around axis k by angle a
            vec3 rotateAroundAxis(vec3 v, vec3 k, float a) {
                float c = cos(a);
                float s = sin(a);
                return v * c + cross(k, v) * s + k * dot(k, v) * (1.0 - c);
            }
            
            // Project world-space direction to screen UV coordinates
            vec2 projectWorldDirToUV(vec3 worldDir) {
                // Convert world direction to view space
                // uCameraMatrix is world-to-view, so we multiply direction vectors by the upper 3x3
                mat3 worldToView = mat3(
                    uCameraMatrix[0].xyz,
                    uCameraMatrix[1].xyz,
                    uCameraMatrix[2].xyz
                );
                vec3 viewDir = normalize(worldToView * worldDir);
                
                // In view space, camera looks down negative Z
                // For a direction vector, we need to project it to the near plane (z = -near)
                // But since we only care about direction, we can use z = -1 as reference
                // Create a point at z = -1 along the view direction
                if (viewDir.z >= 0.0) {
                    // Ray pointing away from camera (shouldn't happen for valid rays)
                    // Return center of screen
                    return vec2(0.5, 0.5);
                }
                
                // Project to z = -1 plane
                float t = -1.0 / viewDir.z;
                vec3 viewPoint = viewDir * t;
                
                // Now use the projection matrix to convert view point to clip space
                // We need the forward projection matrix, not the inverse
                // For standard perspective: clip.x = viewPoint.x * (near / (-viewPoint.z * tan(fov/2) * aspect))
                // But we can compute NDC directly from view space using FOV
                float aspect = uResolution.x / max(uResolution.y, 1.0);
                float fovRad = radians(75.0); // match SceneManager FOV
                float tanHalfFov = tan(fovRad * 0.5);
                
                // Convert view point to NDC
                // NDC = (viewPoint.xy / (-viewPoint.z)) / (tan(fov/2) * scale)
                vec2 ndc = vec2(
                    viewPoint.x / (tanHalfFov * aspect),
                    viewPoint.y / tanHalfFov
                );
                
                // Convert NDC to UV
                vec2 uv = ndc * 0.5 + 0.5;
                
                return uv;
            }
            
            void main() {
                vec2 uv = vUv;
                
                // Step 1: Convert UV to world-space ray direction
                vec3 rayDir = getWorldRayDirection(uv);
                
                // Step 2: Compute vector from camera to black hole
                vec3 toBH = uBlackHolePos - uCameraPos;
                float distToBH = length(toBH);
                vec3 toBHDir = toBH / max(distToBH, EPSILON);
                
                // Step 3: Compute impact parameter (closest approach distance)
                // b = |(BH_pos - cam_pos) × ray_dir|
                vec3 crossProd = cross(toBH, rayDir);
                float b = length(crossProd);
                
                float rs = uSchwarzschildRadius;
                
                // Step 4: Check event horizon capture
                if (b < rs * CAPTURE_FACTOR) {
                    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
                    return;
                }
                
                // Step 5: Compute deflection angle using Schwarzschild formula
                // α = (4GM / c²) / b ≈ (r_s) / b
                // Scale by uDeflectionScale for tuning
                float alpha = DEFLECTION_SCALE_BASE * uDeflectionScale * rs / max(b, rs * 0.1);
                alpha = min(alpha, MAX_DEFLECTION);
                
                // Step 6: Compute rotation axis (perpendicular to ray and BH direction)
                vec3 axis = cross(rayDir, toBHDir);
                float axisLen = length(axis);
                
                // Handle edge case where ray is nearly radial to BH
                if (axisLen < EPSILON) {
                    // Ray is too close to radial - minimal deflection
                    axis = vec3(0.0, 1.0, 0.0); // arbitrary perpendicular axis
                    if (dot(rayDir, toBHDir) > 0.99) {
                        // Ray pointing almost directly at BH - render black
                        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
                        return;
                    }
                } else {
                    axis = normalize(axis);
                }
                
                // Step 7: Rotate ray toward black hole using Rodrigues rotation
                vec3 deflectedDir = rotateAroundAxis(rayDir, axis, alpha);
                
                // Step 8: Project deflected ray back to screen space
                vec2 deflectedUV = projectWorldDirToUV(deflectedDir);
                
                // Step 9: Handle out-of-bounds UV
                if (deflectedUV.x < 0.0 || deflectedUV.x > 1.0 ||
                    deflectedUV.y < 0.0 || deflectedUV.y > 1.0) {
                    // Fall back to original UV if deflection goes off-screen
                    deflectedUV = uv;
                }
                
                deflectedUV = clamp(deflectedUV, 0.0, 1.0);
                
                // Step 10: Sample scene texture at deflected UV
                vec4 color = texture2D(tScene, deflectedUV);
                
                // Step 11: Add Einstein ring enhancement at photon sphere
                float photonSphereRadius = PHOTON_SPHERE_FACTOR * rs;
                float ringWidth = RING_WIDTH * rs;
                float ringFactor = 1.0 - smoothstep(photonSphereRadius - ringWidth,
                                                    photonSphereRadius + ringWidth,
                                                    b);
                if (ringFactor > 0.0) {
                    color.rgb += ringFactor * RING_BRIGHTNESS * vec3(0.4, 0.6, 1.0);
                }
                
                // Step 12: Apply gravitational redshift (stronger for smaller impact parameter)
                float redshift = clamp(rs / max(b, rs), 0.0, 0.3);
                color.rgb *= vec3(1.0 + redshift * 0.5, 1.0, 1.0 - redshift * 0.3);
                
                gl_FragColor = color;
            }
        `;
    }

    update(renderer, scene, camera) {
        // Update uniforms
        if (this.quad && this.quad.material.uniforms) {
            this.quad.material.uniforms.uBlackHolePos.value = this.blackHole.getPosition();
            this.quad.material.uniforms.uBlackHoleMass.value = this.blackHole.getMass();
            this.quad.material.uniforms.uSchwarzschildRadius.value = this.blackHole.getSchwarzschildRadius();
            this.quad.material.uniforms.uResolution.value.set(renderer.domElement.width, renderer.domElement.height);
            this.quad.material.uniforms.uCameraPos.value.copy(camera.position);
            this.quad.material.uniforms.uCameraMatrix.value.copy(camera.matrixWorldInverse);
            this.quad.material.uniforms.uProjectionMatrix.value.copy(camera.projectionMatrixInverse);
            // uDeflectionScale is set in init(), can be adjusted if needed
        }
        
        // Resize render target if needed
        const width = renderer.domElement.width;
        const height = renderer.domElement.height;
        if (this.renderTarget.width !== width || this.renderTarget.height !== height) {
            this.renderTarget.setSize(width, height);
        }
    }

    render(renderer, scene, camera) {
        // First pass: render scene to texture
        renderer.setRenderTarget(this.renderTarget);
        renderer.render(scene, camera);
        
        // Second pass: render lensing shader
        renderer.setRenderTarget(null);
        renderer.render(this.scene2D, new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1));
    }

    dispose() {
        if (this.renderTarget) {
            this.renderTarget.dispose();
        }
        if (this.quad) {
            this.quad.geometry.dispose();
            this.quad.material.dispose();
        }
    }
}
